import axios from "axios";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const BASE = `${API_ROOT}ipd/billing/rules`;

export function getBillingRulesHeaders(includeJson = false) {
  const headers = {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "App-Mode": sessionStorage.getItem("APP_MODE") || sessionStorage.getItem("appMode") || "SaaS",
    "Org-Code": sessionStorage.getItem("ORG_CODE") || sessionStorage.getItem("orgCode") || "",
    Username: sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "",
    "User-Access-Key": sessionStorage.getItem("USER_ACCESS_KEY") || sessionStorage.getItem("accessKey") || "",
  };
  if (includeJson) headers["Content-Type"] = "application/json";
  return headers;
}

export function trimId(id) {
  return String(id ?? "").replace(/\s+/g, "").trim();
}

function assertSuccess(data) {
  if (data?.status === "Failed") {
    const err = new Error(data.message ?? "Request failed");
    err.apiResponse = data;
    throw err;
  }
  return data;
}

async function request(method, path, body) {
  try {
    const res = await axios({
      method,
      url: `${BASE}${path}`,
      data: body,
      headers: getBillingRulesHeaders(body != null),
    });
    return assertSuccess(res.data);
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    if (err.response?.data?.status === "Failed") {
      throw new Error(err.response.data.message ?? "Request failed");
    }
    throw err;
  }
}

// `ruleDomain` narrows the condition fields to the ones that domain's calculator can
// actually read. It matters for DOCTOR_PAYOUT: that calculator builds its own context and
// cannot see department.*/payer.*/ward.* etc, so offering those fields lets someone save a
// rule that silently never fires. Omitted = the full rule-engine catalog.
export function getRulesCatalog(ruleDomain) {
  const qs = ruleDomain ? `?rule_domain=${encodeURIComponent(ruleDomain)}` : "";
  return request("GET", `/catalog/${qs}`);
}

export function listRuleSets() {
  return request("GET", "/rule-sets/").then((d) => d.rule_sets?.items ?? []);
}

export function createRuleSetWizard(body) {
  return request("POST", "/rule-sets/wizard/", body);
}

export function getRuleSet(id) {
  return request("GET", `/rule-sets/${trimId(id)}/`);
}

export function updateRuleSet(id, body) {
  return request("PUT", `/rule-sets/${trimId(id)}/`, body);
}

export function activateRuleSet(id) {
  return request("PATCH", `/rule-sets/${trimId(id)}/activate/`);
}

export function deactivateRuleSet(id) {
  return request("PATCH", `/rule-sets/${trimId(id)}/deactivate/`);
}

export function listRuleDefinitions(ruleSetId) {
  const id = trimId(ruleSetId);
  return request("GET", `/rule-definitions/?rule_set_id=${encodeURIComponent(id)}`)
    .then((d) => d.rule_definitions?.items ?? []);
}

export function getRuleDefinition(id) {
  return request("GET", `/rule-definitions/${trimId(id)}/`).then((d) =>
    d.rule_definition ?? d.rule_definitions ?? d,
  );
}

export function createRuleDefinition(body) {
  const payload = { ...body, rule_set_id: trimId(body.rule_set_id) };
  return request("POST", "/rule-definitions/", payload).then((d) =>
    d.rule_definition ?? d,
  );
}

export function updateRuleDefinition(id, body) {
  const payload = { ...body, rule_set_id: trimId(body.rule_set_id) };
  return request("PUT", `/rule-definitions/${trimId(id)}/`, payload).then((d) =>
    d.rule_definition ?? d,
  );
}

export function deactivateRuleDefinition(id) {
  return request("PATCH", `/rule-definitions/${trimId(id)}/deactivate/`);
}

export function evaluateManual(body) {
  return request("POST", "/evaluate/manual/", body);
}

/** Map wizard form draft → API wizard payload */
export function wizardDraftToApiBody(draft) {
  return {
    code: draft.code,
    name: draft.name,
    domain: draft.domain,
    description: draft.description ?? "",
    priority: draft.priority,
    effective_from: draft.effective_from,
    effective_to: draft.effective_to || null,
    active: draft.active,
    encounterTypes: draft.encounterTypes,
    payerTypes: draft.payerTypes,
    starter_templates: draft.templates,
  };
}
